import React, { useState, useEffect } from 'react';
import { X, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';

const TOUR_STEPS = [
  {
    id: 'welcome',
    title: 'Bienvenue sur Oknî ! 👋',
    description: 'Découvrez comment échanger des services basés sur le temps avec votre communauté.',
    position: 'center'
  },
  {
    id: 'services',
    title: 'Proposez vos services 💼',
    description: 'Partagez vos compétences et gagnez des crédits temps.',
    target: '[data-tour="services-link"]',
    position: 'bottom'
  },
  {
    id: 'search',
    title: 'Trouvez de l\'aide 🔍',
    description: 'Recherchez des services dont vous avez besoin dans votre région.',
    target: '[data-tour="search"]',
    position: 'bottom'
  },
  {
    id: 'credits',
    title: 'Vos crédits temps ⏰',
    description: '1 heure = 1 crédit. Vous commencez avec 10 crédits gratuits !',
    target: '[data-tour="credits"]',
    position: 'bottom'
  },
  {
    id: 'chatbot',
    title: 'Besoin d\'aide ? 💬',
    description: 'Notre chatbot est là pour répondre à vos questions.',
    target: 'button[aria-label="Ouvrir le chatbot"]',
    position: 'left'
  }
];

export default function OnboardingTour({ onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const hasSeenTour = localStorage.getItem('onboarding_completed');
    if (!hasSeenTour) {
      setTimeout(() => setIsVisible(true), 1000);
    }
  }, []);

  const handleNext = () => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  const handleComplete = () => {
    localStorage.setItem('onboarding_completed', 'true');
    setIsVisible(false);
    if (onComplete) onComplete();
  };

  if (!isVisible) return null;

  const step = TOUR_STEPS[currentStep];

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/30 z-40 pointer-events-none" />
      
      {/* Tour Card (non-blocking: header stays clickable) */}
      <div className="fixed z-40 bottom-6 left-4 right-4 sm:left-6 sm:right-auto" data-testid="onboarding-tour">
        <Card className="w-full sm:w-96 rounded-2xl shadow-2xl animate-in fade-in zoom-in duration-300">
          <CardContent className="p-6">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-bold">{step.title}</h3>
              <Button variant="ghost" size="icon" onClick={handleSkip}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <p className="text-muted-foreground mb-6">{step.description}</p>
            
            <div className="flex items-center justify-between">
              <div className="flex gap-1">
                {TOUR_STEPS.map((_, idx) => (
                  <div
                    key={idx}
                    className={`h-2 w-2 rounded-full transition-colors ${
                      idx === currentStep ? 'bg-primary' : 'bg-muted'
                    }`}
                  />
                ))}
              </div>
              
              <div className="flex gap-2">
                <Button variant="ghost" onClick={handleSkip} data-testid="onboarding-skip-btn">
                  Passer
                </Button>
                <Button onClick={handleNext} className="btn-primary">
                  {currentStep < TOUR_STEPS.length - 1 ? (
                    <>
                      Suivant <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  ) : (
                    <>
                      Terminer <CheckCircle className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
